import {
  findShownameShowGroupByShowName,
  getPunchupShowIdsByTixologiIds,
  getShowGroupLinkBySlugPublic,
} from "@punchup/data/server";

import { PublicShowGroupLink } from "@punchup/data/models";
import { SupabaseClient } from "@supabase/supabase-js";
import type { VenuePageTimezones } from "@punchup/data/models/venue_pages";
import { TixologiResumedEvent } from "@punchup/data/legacy/tixologi";
import { VenuePagesRow } from "../data/venue_pages";
import dayjs from "@punchup/utils/dayjs";
import {
  ShowGroupLineupMember,
  toLineupMembers,
} from "../client/components/show-groups/utils";
import {
  type ShowWithComedian,
  getShowsByIds,
  getShowsForVenuePage,
} from "@punchup/data/legacy/shows";
import { getTixologiEvents } from "@punchup/data/legacy/hooks";
import { getVenuePage, resolveVenueTimezone } from "../data/venue_pages";
import { upcomingShowsCutoff } from "./upcomingShowsCutoff";
import {
  buildEventLineups,
  buildTixologiAvailabilityMaps,
  dedupeEventsByNameAndDate,
  expandEventDays,
  filterAndSortUpcomingEvents,
} from "./showGroupEvents";
import { logException } from "@punchup/sentry";
import { slugify } from "@punchup/utils";

export interface VenueShowGroupData {
  series: PublicShowGroupLink;
  events: TixologiResumedEvent[];
  venueName: string;
  venueAddress: string | null;
  /**
   * Fallback IANA timezone for events whose Tixologi record is missing its
   * own `time_zone`. Per-event picking happens downstream — `groupEventsByDate`,
   * `formatEventDateHeader`, and `ShowGroupEventCard` all use `ev.time_zone`
   * first and reach for this only when a specific event lacks it. Chosen as
   * `venue_pages.timezones[0].timezone`; arbitrary for multi-location pages
   * but only observable when an event's own tz is missing (rare in practice).
   */
  venueTimezone: string;
  /**
   * Map from Tixologi event ID (stringified) to punchup comedian_shows UUID.
   * Used to link event titles to their punchup.live event pages.
   */
  eventPageIds: Record<string, string>;
  /**
   * Per-event lineups keyed by Tixologi event ID (stringified). Empty array for
   * events with no resolvable comedian_shows lineup. Used to render the lineup
   * on each card when `sharedLineup` is null.
   */
  eventLineups: Record<string, ShowGroupLineupMember[]>;
  /**
   * Ordered lineup shared by every show in the group, present only when all
   * shows have an identical, non-empty lineup. When non-null the page shows a
   * single "Lineup" container at the top instead of a lineup per card.
   */
  sharedLineup: ShowGroupLineupMember[] | null;
  /**
   * Punchup show (with comedians + venue owner id) keyed by Tixologi event ID,
   * populated only for events that are sold out AND resolve to a comedian_shows
   * row with a venue_id. Feeds the sold-out `WaitlistButton` on each card (the
   * same waitlist as `TourDateRow`); reuses the `getShowsByIds` map already
   * fetched for lineups.
   */
  waitlistShows: Record<string, ShowWithComedian>;
  /**
   * Authoritative sold-out per Tixologi event ID, from the same Tixologi
   * availability overlay the venue page grid/carousel use (manual `is_sold_out`
   * flag OR live bulk-availability). Absent for events that don't resolve to a
   * punchup show — the card falls back to the event's ticket tiers there.
   */
  soldOutByEventId: Record<string, boolean>;
  /**
   * External ticket URL per event key, present only for synthesized
   * externally-ticketed shows (the email featured-shows page). The card
   * renders a plain "Buy Tickets" anchor instead of Tixologi fast checkout;
   * a null value means external with no ticket link (the card links to the
   * punchup show page instead). Absent entirely for Tixologi-backed events.
   */
  externalTicketLinks?: Record<string, string | null>;
}

export type GetVenueShowGroupDataResult =
  | { success: true; data: VenueShowGroupData }
  | { success: false; notFound: true }
  | { success: false; redirectToShowId: string };

/**
 * Pool of Tixologi events for a venue, used both for auto-resolution and for
 * the normal showname/recurring filter path.
 *
 * Mirrors the venue homepage's show set — including its start-of-local-today
 * cutoff (`upcomingShowsCutoff`), so the drill-down never surfaces a show the
 * grid already dropped: read `comedian_shows` for every `location_id` the
 * venue page covers, run producer-page filtering (so a producer venue only
 * surfaces shows it has explicitly linked, while a non-producer venue picks
 * up its location's pre-launch shows), then bridge to Tixologi via each
 * show's `tixologi_event_id`. Skips shows without a Tixologi id (legacy /
 * non-Tixologi-ticketed).
 */
export async function fetchVenueEventPool(
  supabase: SupabaseClient,
  venue: VenuePagesRow
): Promise<TixologiResumedEvent[]> {
  if (!venue.location_ids?.length) return [];

  const startDate = upcomingShowsCutoff(
    (venue.timezones ?? []) as VenuePageTimezones
  );
  const filtered = await getShowsForVenuePage(supabase, venue.id, {
    startDate,
  });

  return eventPoolFromShows(filtered);
}

/**
 * Bridge a set of comedian_shows rows to their Tixologi events. Skips shows
 * without a Tixologi id (legacy / non-Tixologi-ticketed).
 */
async function eventPoolFromShows(
  shows: ShowWithComedian[]
): Promise<TixologiResumedEvent[]> {
  const ids = [
    ...new Set(
      shows.map((s) => s.tixologi_event_id).filter((id): id is string => !!id)
    ),
  ];
  if (!ids.length) return [];

  const response = await getTixologiEvents({
    ids,
    date: "live_or_scheduled",
    perPage: -1,
  });
  return Array.isArray(response) ? response : [];
}

type AutoSlugResolution =
  | { event: TixologiResumedEvent }
  | { redirectToShowId: string }
  | null;

/**
 * Resolve an auto-resolution slug (no admin-created show_group_link row) to
 * an upcoming event at the venue, trying in order:
 *
 * 1. A Tixologi event whose `name` slugifies to the slug.
 * 2. Title-drift bridge: venue-page links are built from `comedian_shows.title`
 *    (see TourDateRow), which can drift from the Tixologi event name when one
 *    side is renamed. Match the punchup title and resolve its
 *    `tixologi_event_id` in the pool so those links keep working.
 * 3. Externally-ticketed shows (Eventbrite, TicketWeb, etc.) have a null
 *    `tixologi_event_id` and are excluded from the pool. The venue page still
 *    links them to `/v/[venue]/shows/[slug]`, so without this fallback those
 *    URLs 404. Returns the matching show id so the page can redirect to
 *    `/show/[showId]`, which delegates to `TicketRedirect` and handles
 *    external ticket flow.
 */
async function resolveAutoSlug(
  supabase: SupabaseClient,
  venue: VenuePagesRow,
  groupSlug: string
): Promise<AutoSlugResolution> {
  if (!venue.location_ids?.length) return null;

  try {
    const startDate = upcomingShowsCutoff(
      (venue.timezones ?? []) as VenuePageTimezones
    );
    const shows = await getShowsForVenuePage(supabase, venue.id, {
      startDate,
    });
    const pool = await eventPoolFromShows(shows);

    const byEventName = pool.find(
      (ev) => slugify(ev.name.trim()) === groupSlug
    );
    if (byEventName) return { event: byEventName };

    const titleMatches = shows.filter(
      (s) => !!s.title && slugify(s.title.trim()) === groupSlug
    );

    const bridged = titleMatches.find((s) => s.tixologi_event_id);
    if (bridged) {
      const event = pool.find(
        (ev) => String(ev.id) === bridged.tixologi_event_id
      );
      if (event) return { event };
    }

    const external = titleMatches.find((s) => !s.tixologi_event_id);
    if (external) return { redirectToShowId: external.id };

    return null;
  } catch (error) {
    logException(error instanceof Error ? error : new Error(String(error)), {
      tags: { source: "resolveAutoSlug" },
      extra: { venueId: venue.id, venueSlug: venue.slug, groupSlug },
    });
    return null;
  }
}

export async function getVenueShowGroupData(
  supabase: SupabaseClient,
  venueSlug: string,
  groupSlug: string
): Promise<GetVenueShowGroupDataResult> {
  const venue = await getVenuePage(supabase, { slug: venueSlug }).catch(
    () => null
  );
  if (!venue) return { success: false, notFound: true };

  const fromDb = await getShowGroupLinkBySlugPublic({
    venuePageId: venue.id,
    slug: groupSlug,
  });

  // Auto-resolution: if no admin-created row exists, try to interpret the
  // slug as a slugified show title and synthesize a showname-style series.
  // Powers the "click any show's title on the venue page to see all events
  // with that title" flow without requiring the admin to pre-create a link.
  const autoResolved = !fromDb;
  let inheritedFrom: string | null = null;
  let series: PublicShowGroupLink;
  if (fromDb) {
    series = fromDb;
  } else {
    const resolved = await resolveAutoSlug(supabase, venue, groupSlug);
    if (!resolved) {
      return { success: false, notFound: true };
    }
    if ("redirectToShowId" in resolved) {
      return { success: false, redirectToShowId: resolved.redirectToShowId };
    }
    const match = resolved.event;

    // If an admin has already configured a showname link for this exact
    // show name (possibly under a different custom title/slug), inherit
    // their customization — Facebook Pixel, title, etc. — so grid
    // clicks transparently pick up the admin's setup.
    const configured = await findShownameShowGroupByShowName({
      venuePageId: venue.id,
      showName: match.name,
    }).catch(() => null);

    if (configured) {
      inheritedFrom = configured.id;
      // Keep the URL the user landed on (groupSlug), but inherit the
      // admin's pixel + title from their configured row.
      series = {
        ...configured,
        id: `auto-${venue.id}-${groupSlug}`,
        slug: groupSlug,
      };
    } else {
      series = {
        id: `auto-${venue.id}-${groupSlug}`,
        venue_page_id: venue.id,
        title: match.name,
        slug: groupSlug,
        link_type: "showname",
        event_ids: null,
        start_date: null,
        end_date: null,
        day_of_week: null,
        show_time: null,
        show_name: match.name,
        facebook_pixel_id: null,
        created_at: dayjs(0).toISOString(),
        updated_at: dayjs(0).toISOString(),
      };
    }
  }

  let events: TixologiResumedEvent[] = [];
  let beforeDateFilterCount = 0;
  let rawFetchCount = 0;

  // Per-event tz picking happens downstream (`groupEventsByDate` +
  // `formatEventDateHeader` + `ShowGroupEventCard` all read `ev.time_zone`
  // per event). This variable is only the fallback for events missing their
  // own `time_zone` — rare in practice, and using `timezones[0]` is an
  // arbitrary but harmless pick when it does happen.
  const timezones = (venue.timezones ?? []) as VenuePageTimezones;
  const venueTimezone = resolveVenueTimezone(timezones, null);

  try {
    if (series.link_type === "weekend") {
      if (series.event_ids && series.event_ids.length > 0) {
        const response = await getTixologiEvents({
          ids: series.event_ids.map(String),
          date: "live_or_scheduled",
          perPage: -1,
        });
        events = Array.isArray(response) ? response : [];
        rawFetchCount = events.length;
      }
    } else {
      const all = await fetchVenueEventPool(supabase, venue);
      rawFetchCount = all.length;

      // Name filter for showname runs BEFORE day-expansion so we don't expand
      // unrelated events. For recurring we defer filtering to AFTER expansion
      // because a multi-day event's master start_date may not land on the
      // target weekday even when individual day entries do.
      if (series.link_type === "showname") {
        const target = series.show_name?.trim().toLowerCase();
        if (target) {
          events = all.filter((ev) => ev.name.trim().toLowerCase() === target);
        }
      } else {
        events = all;
      }
    }
  } catch (error) {
    logException(error instanceof Error ? error : new Error(String(error)), {
      tags: { source: "getVenueShowGroupData" },
      extra: {
        venueSlug,
        groupSlug,
        linkType: series.link_type,
      },
    });
    events = [];
  }

  events = expandEventDays(events);

  // Apply day-of-week / time filter after expansion so recurring links only
  // keep matching individual dates. Falls back to the venue's timezone when
  // the event record itself doesn't carry a time_zone — without this the
  // comparison would happen in UTC and day-of-week could drift by one day.
  if (series.link_type === "recurring") {
    const target = series.day_of_week;
    const time = series.show_time;
    events = events.filter((ev) => {
      const tz = ev.time_zone || venueTimezone;
      const d = dayjs(ev.start_date).tz(tz);
      if (d.day() !== target) return false;
      if (!time) return true;
      return d.format("HH:mm") === time;
    });
  }

  events = dedupeEventsByNameAndDate(events);

  beforeDateFilterCount = events.length;

  events = filterAndSortUpcomingEvents(events, timezones);

  const venueAddress = events[0]?.street_address ?? null;

  const uniqueTixologiIds = [...new Set(events.map((ev) => String(ev.id)))];
  const eventPageIds = await getPunchupShowIdsByTixologiIds(
    uniqueTixologiIds
  ).catch((err) => {
    logException(err instanceof Error ? err : new Error(String(err)), {
      tags: { source: "getVenueShowGroupData:eventPageIds" },
    });
    return {} as Record<string, string>;
  });

  // Resolve each show's comedian lineup so the page can either surface a single
  // shared "Lineup" container (when every show has the same lineup) or render
  // the lineup per card. Keyed by Tixologi event ID for the per-card path.
  const showIds = [...new Set(Object.values(eventPageIds))];
  const lineupByShowId = new Map<string, ShowGroupLineupMember[]>();
  let showsById = new Map<string, ShowWithComedian>();
  if (showIds.length > 0) {
    try {
      showsById = await getShowsByIds(showIds);
      for (const [showId, show] of showsById) {
        lineupByShowId.set(showId, toLineupMembers(show.show_comedians));
      }
    } catch (err) {
      logException(err instanceof Error ? err : new Error(String(err)), {
        tags: { source: "getVenueShowGroupData:lineups" },
      });
    }
  }

  const { soldOutByEventId, waitlistShows } =
    await buildTixologiAvailabilityMaps(Array.from(showsById.values()));

  const { eventLineups, sharedLineup } = buildEventLineups(
    events,
    eventPageIds,
    lineupByShowId
  );

  return {
    success: true,
    data: {
      series,
      events,
      venueName: venue.name,
      venueAddress,
      venueTimezone,
      eventPageIds,
      eventLineups,
      sharedLineup,
      waitlistShows,
      soldOutByEventId,
    },
  };
}
